import type { ApprovedOfferDocument } from './approvedOfferDocument';

export interface QuickEstimateSnapshot<TForm, TResult, TManualItem, TDecision> {
  version: 1;
  approvedOffer?: ApprovedOfferDocument;
  form: TForm;
  result: TResult;
  manualItems: TManualItem[];
  marginShieldDecisions: Record<string, TDecision>;
}

export function createQuickEstimateSnapshot<TForm, TResult, TManualItem, TDecision>(
  input: Omit<QuickEstimateSnapshot<TForm, TResult, TManualItem, TDecision>, 'version'>
): QuickEstimateSnapshot<TForm, TResult, TManualItem, TDecision> {
  return JSON.parse(JSON.stringify({ version: 1, ...input })) as QuickEstimateSnapshot<
    TForm,
    TResult,
    TManualItem,
    TDecision
  >;
}

export function readQuickEstimateSnapshot<TForm, TResult, TManualItem, TDecision>(
  value: unknown
): QuickEstimateSnapshot<TForm, TResult, TManualItem, TDecision> | null {
  if (!value || typeof value !== 'object') return null;
  const snapshot = value as Partial<QuickEstimateSnapshot<TForm, TResult, TManualItem, TDecision>>;
  if (snapshot.version !== 1 || !snapshot.form || !snapshot.result) return null;
  const manualItems = snapshot.manualItems === undefined ? [] : snapshot.manualItems;
  const marginShieldDecisions = snapshot.marginShieldDecisions === undefined ? {} : snapshot.marginShieldDecisions;
  if (!Array.isArray(manualItems) || !isRecord(marginShieldDecisions)) return null;
  return { ...snapshot, manualItems, marginShieldDecisions } as QuickEstimateSnapshot<TForm, TResult, TManualItem, TDecision>;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

/** Validate the persisted Quick screen before rendering or allowing an update. No reconstruction. */
export function isUsableQuickProjectSnapshot(value: unknown): boolean {
  const snapshot = readQuickEstimateSnapshot(value);
  if (!snapshot || !isRecord(snapshot.form) || !isRecord(snapshot.result)) return false;
  const form = snapshot.form;
  const result = snapshot.result;
  if (form.pavingMode !== undefined && !['surface', 'edges_only'].includes(form.pavingMode as string)) return false;
  if (![
    'readyWorkingArea', 'manualHeight', 'roomLength', 'roomWidth', 'doorCount', 'largeOpeningsArea',
    'apartmentArea', 'apartmentHeight', 'apartmentDoorCount', 'apartmentLargeOpeningsArea', 'bathroomArea',
    'pavingArea', 'pavingLength', 'pavingWidth', 'curbLength', 'customScope', 'customLaborRate',
    'materialDiscountPercent', 'materialClientPrice', 'contractorName', 'contractorNip', 'contractorContact',
  ].every((key) => typeof form[key] === 'string') ||
      typeof form.includeCeiling !== 'boolean' || typeof form.bathroomCeilingOnly !== 'boolean') return false;
  const enums: Record<string, readonly string[]> = {
    surfaceMode: ['ready', 'room', 'apartment', 'wall'],
    manualScope: ['painting', 'plaster_painting', 'scrape_prime_plaster_paint', 'panels', 'bathroom', 'paving', 'other'],
    laborRateMode: ['suggested', 'custom'],
    materialMode: ['client', 'contractor', 'info'],
    vatMode: ['no_vat', 'vat_8', 'vat_23', 'confirm'],
    apartmentLayout: ['open', 'standard', 'many_rooms'],
    pavingThickness: ['6cm', '8cm', 'confirm'],
    pavingSubbase: ['ready', 'todo', 'confirm'],
    pavingTransport: ['client', 'contractor', 'confirm'],
  };
  if (!Object.entries(enums).every(([key, values]) => values.includes(form[key] as string))) return false;
  if (!enums.manualScope.includes(result.scope as string) ||
      !enums.materialMode.includes(result.materialMode as string) ||
      !enums.vatMode.includes(result.vatMode as string)) return false;
  if (!['scopeLabel', 'inputSourceLabel', 'floorAreaSource', 'scopeSource']
    .every((key) => typeof result[key] === 'string')) return false;
  if (!['areaM2', 'priceFrom', 'priceTo', 'materialDiscountPercent']
    .every((key) => typeof result[key] === 'number' && Number.isFinite(result[key]))) return false;
  if (!['aiObservations', 'risks'].every((key) =>
    Array.isArray(result[key]) && result[key].every((entry: unknown) => typeof entry === 'string'))) return false;
  if (!isRecord(result.engineInput) || !isRecord(result.estimate) || !isRecord(result.estimate.meta)) return false;
  const estimate = result.estimate;
  const meta = result.estimate.meta;
  if (typeof estimate.cost !== 'number' || !Number.isFinite(estimate.cost) ||
      typeof result.engineInput.height !== 'number' || !Number.isFinite(result.engineInput.height)) return false;
  if (!['floorArea', 'totalWorkingArea', 'wallsNet', 'ceilingArea'].every((key) =>
    typeof meta[key] === 'number' && Number.isFinite(meta[key]))) return false;
  const visionSummary = result.visionSummary;
  if (!isRecord(visionSummary) || !['chips', 'manualChecks'].every((key) =>
    Array.isArray(visionSummary[key]) && visionSummary[key].every((entry: unknown) => typeof entry === 'string'))) return false;
  const isItem = (item: unknown): boolean => isRecord(item) &&
    ['id', 'label', 'unit', 'calculation', 'pricingMode'].every((key) => typeof item[key] === 'string') &&
    ['quantity', 'unitPrice', 'unitRateNetto', 'total', 'totalNetto'].every((key) =>
      typeof item[key] === 'number' && Number.isFinite(item[key]) && item[key] >= 0);
  if (!Array.isArray(estimate.items) || !estimate.items.every(isItem) ||
      !Array.isArray(estimate.leaks)) return false;
  if (meta.materialSuggestions !== undefined && (!Array.isArray(meta.materialSuggestions) ||
      !meta.materialSuggestions.every((item: unknown) => isRecord(item) &&
        typeof item.label === 'string' && typeof item.quantity === 'number' && Number.isFinite(item.quantity)))) return false;
  if (!snapshot.manualItems.every((item) => isItem(item) && isRecord(item) &&
      item.source === 'manual' && ['labor', 'material', 'auxiliary'].includes(item.itemType as string))) return false;
  if (!isRecord(snapshot.marginShieldDecisions) || !Object.values(snapshot.marginShieldDecisions)
    .every((decision) => ['suggested', 'added', 'skipped', 'checked'].includes(decision as string))) return false;
  return true;
}
